
import { Component, Vue } from 'vue-property-decorator'
import { ListDepts, ListProjects } from '@/api/orgAdmin'
import { ListOrgUsers } from '@/api/orgAdmin'
import { BatchAssignRepairDept, ListLeaks} from '@/api/leaks'
import { ILeakPoint, IAllDeptInfo, IAllUserInfo, DeptType, IProject, IDept, LeakStatus, IPoint } from '@/api/types'

// 导入组件
import LeakMapList from '@/views/leaks/components/LeakMapList/index.vue'
import { HasPer } from '@/utils/check'
import { UserModule } from '@/store/modules/user'

@Component({
  name: 'LeakMapIndex',
  components: {
    LeakMapList,
  }
})
export default class extends Vue {
  // 对话框相关
  private shouldReloadMap = false

  // 选项数据
  private deptList: IAllDeptInfo[] = []
  private userList: IAllUserInfo[] = []
  private projectList: IProject[] = []


  created() {
    this.getOptions()
  }

  private async getOptions() {
    await this.getDeptList()
    await this.getUserList()
    await this.getProjectList()
  }

  private async getDeptList() {
    try {
      const response = await ListDepts()
      this.deptList = (response.data || [])
    } catch (error) {
      console.error('获取部门列表失败', error)
    }
  }

  // 只能自己创建漏点
  private get detectUsers() : IAllUserInfo[] {
    return this.userList.filter(user => user.user.id === UserModule.userInfo?.id)
  }

  private get repairUsers() : IAllUserInfo[] {
    return this.userList.filter(user => user.permissions.includes('漏点维修'))
  }

  private get repairDepts() : IDept [] {
    return this.deptList.filter(dept => dept.dept.type === DeptType.Repair).map(dept=>dept.dept)
  }

  private get defaultActiveLayers() : string[] {
    if (HasPer(['漏点管理'])) {
      return ['待认领', '维修中', '已处理', '待分配']
    } else if (HasPer(['漏点检测', '上帝'])) {
      return ['未上报','待认领', '维修中', '已处理', '待分配']
    } else {
      return ['待认领', '维修中', '已处理']
    }
  }

  private get visibleLayerOptions(): string[] {
    if (!HasPer(['漏点管理', '漏点检测', '上帝'])) {
      return ['待认领', '维修中', '已处理']
    }
    return ['未上报','待分配', '待认领', '维修中', '已处理']
  }

  private async getUserList() {
    try {
      const { data } = await ListOrgUsers()
      this.userList = data.items || []
    } catch (error) {
      console.error('获取用户列表失败', error)
    }
  }

  private async getProjectList() {
      const { data } = await ListProjects()
      this.projectList = data.map((project:any) => project.project)
  }

  // 刷新列表
  private refreshList() {
    this.shouldReloadMap = true
  }

  // 处理详情
  private handleDetail(leakId: number) {
    if (!leakId) {
      this.$message.warning('无效的漏点ID')
      return
    }

    // 查找项目信息和用户信息
    this.findLeakAndNavigate(leakId)
  }


  // 查找漏点信息并导航
  private async findLeakAndNavigate(leakId: number, mode: string = 'view') {
    try {
      // 导航到详情页面，使用命名路由
      this.$router.push({
        name: 'LeakDetail',
        params: {
          id: leakId.toString(),
          mode: mode,
          projects: JSON.stringify(this.projectList),
          checkUsers: JSON.stringify(this.detectUsers),
          repairUsers: JSON.stringify(this.repairUsers),
          repairDepts: JSON.stringify(this.repairDepts)
        }
      })
    } catch (error) {
      console.error('获取漏点信息失败', error)
      this.$message.error('获取漏点信息失败')
    }
  }

  // 处理创建漏点 - 使用页面导航代替弹窗
  private navigateToCreatePage(data: { position: IPoint, address: string }) {
    // 添加检测人员列表和位置参数到路由的params中，使用命名路由
    const params: any = {
      detectUsers: JSON.stringify(this.detectUsers)
    }

    // 保存位置信息
    if (data && data.position) {
      params.position = JSON.stringify(data.position)
      params.address = data.address || ''
    }

    // 导航到创建页面
    this.$router.push({
      name: 'CreateLeak',
      params
    })
  }

  // 处理批量分配
  private async handleBatchAssign(params: { leakIds: number[], deptId: number }) {
    if (!params.leakIds.length) {
      this.$message({
        message: '没有可分配的漏点',
        type: 'warning'
      })
      return
    }

    try {
      // 显示加载提示
      this.$message({
        message: '正在批量分配漏点...',
        type: 'info'
      })

      // 获取漏点详情以验证状态
      const { data: leakData } = await ListLeaks({
        status: [LeakStatus.NeedAssignRepairDept], // 只查询待分配状态的漏点
        page: 1,
        pageSize: 1000 // 设置一个足够大的值，确保获取所有匹配的漏点
      })

      // 筛选出当前仍处于待分配状态的漏点
      const validLeakIds = params.leakIds.filter(id =>
        leakData.items.some((leak: ILeakPoint) => leak.id === id && leak.leakStatus === LeakStatus.NeedAssignRepairDept)
      )

      if (!validLeakIds.length) {
        this.$message({
          message: '所选漏点已不处于待分配状态，无法执行批量分配',
          type: 'warning'
        })
        return
      }

      if (validLeakIds.length < params.leakIds.length) {
        this.$message({
          message: `原选择${params.leakIds.length}个漏点，但只有${validLeakIds.length}个漏点当前处于待分配状态`,
          type: 'info'
        })
      }

      // 调用批量分配API
      await BatchAssignRepairDept({
        leakIds: validLeakIds,
        deptId: Number(params.deptId)
      })

      // 操作成功提示
      this.$message({
        message: `成功将${validLeakIds.length}个漏点分配给维修部门`,
        type: 'success'
      })

      // 刷新列表
      this.refreshList()
    } catch (error) {
      // 处理错误
      console.error('批量分配漏点失败', error)
      this.$message({
        message: '批量分配失败，请重试',
        type: 'error'
      })
    }
  }
}
